import React from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  Divider,
  Table,
  Chip,
  Button,
  Stack,
  Card,
  Alert,
  Link,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
} from '@mui/joy';
import { PaperPlaneTilt as SendIcon, DownloadSimple as DownloadIcon, XCircle as CancelIcon, Copy as ContentCopyIcon, ArrowsClockwise as SyncIcon } from '@phosphor-icons/react';
import { ordersApi, type Order } from '../../lib/distributorsApi';
import { useNotification } from '../../contexts/NotificationContext';
import OrderStatusTimeline from './OrderStatusTimeline';
import SubmissionResolution from './SubmissionResolution';
import { appointmentTypeLabel, deliveryServiceLabel } from './abcConstants';
import { SRS_CHECKOUT_DISCLAIMER, SRS_SERVICE_LABELS, SRS_TBD_DATE } from './srsOrdering';

interface Props {
  open: boolean;
  onClose: () => void;
  order: Order | null;
  onRefresh?: () => void;
  onDuplicate?: (order: Order) => void;
}

export default function OrderDetailModal({ open, onClose, order, onRefresh, onDuplicate }: Props) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = React.useState(false);
  const [recipientEmail, setRecipientEmail] = React.useState<string | null>(null);
  React.useEffect(() => {
    setRecipientEmail(null);
    setFullOrder(null);
  }, [open, order?.id]);
  const [fullOrder, setFullOrder] = React.useState<Order | null>(null);
  const [fetchingOrder, setFetchingOrder] = React.useState(false);
  const requestVersion = React.useRef(0);

  React.useEffect(() => {
    if (!open || !order) return;
    fetchFullOrderDetails();
    const refresh = () => { if (document.visibilityState === 'visible') void fetchFullOrderDetails(true); };
    const timer = order.submission_method === 'api' ? window.setInterval(refresh, 30_000) : undefined;
    document.addEventListener('visibilitychange', refresh);
    return () => {
      ++requestVersion.current;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [open, order?.id]);

  const fetchFullOrderDetails = async (silent = false) => {
    if (!order) return;
    
    const version = ++requestVersion.current;
    if (!silent) setFetchingOrder(true);
    try {
      const response = await ordersApi.get(order.id);
      if (version !== requestVersion.current) return;
      setFullOrder(response);
    } catch (err: any) {
      if (version !== requestVersion.current || silent) return;
      showError('Failed to load order details');
      setFullOrder(order);
    } finally {
      if (version === requestVersion.current) setFetchingOrder(false);
    }
  };

  if (!order) return null;

  const displayOrder = fullOrder || order;
  const isSrs = displayOrder.distributor_details?.integration_type === 'api_srs';
  const deliveryEmail =
    recipientEmail ?? displayOrder.distributor_details?.default_order_email ?? '';

  const getStatusColor = (status: string) => {
    const colors: Record<string, any> = {
      draft: 'neutral',
      submitted: 'primary',
      processing: 'warning',
      completed: 'success',
      cancelled: 'danger',
    };
    return colors[status] || 'neutral';
  };

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      // Date-only distributor schedules are calendar dates, not UTC instants.
      ...(/^\d{4}-\d{2}-\d{2}$/.test(date) ? { timeZone: 'UTC' } : {}),
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  // API orders: already submitted through the API, or drafts whose
  // distributor is API-integrated (their submit goes to the vendor API).
  const isApiOrder =
    displayOrder?.submission_method === 'api'
    || (displayOrder?.status === 'draft'
      && (displayOrder?.distributor_details?.integration_type || '').startsWith('api_'));

  const handleSubmitOrder = async () => {
    if (!displayOrder || displayOrder.status !== 'draft') return;
    if (!isApiOrder && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(deliveryEmail.trim())) {
      showError('Enter a valid distributor email address before submitting.');
      return;
    }

    setLoading(true);
    try {
      // API drafts carry their delivery details — a bare submit uses them.
      const result = await ordersApi.submit(
        displayOrder.id,
        isApiOrder
          ? {}
          : { generate_pdf: true, send_email: true, recipient_email: deliveryEmail.trim() },
      );
      if (!isApiOrder && result.email_result?.success === false) {
        showError(`Order saved, but email failed: ${result.email_result.message}`);
        onRefresh?.();
        await fetchFullOrderDetails();
        return;
      }
      showSuccess(
        isApiOrder && result.confirmation_number
          ? `Order submitted — confirmation #${result.confirmation_number}`
          : `Order ${displayOrder.order_number} submitted successfully!`,
      );
      if (onRefresh) onRefresh();
      onClose();
    } catch (err: any) {
      const data = err.response?.data || err.responseData || {};
      showError(data.api_error || data.error || data.detail || 'Failed to submit order');
      // Refresh so a stored api_error shows in the modal.
      fetchFullOrderDetails();
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    if (!displayOrder) return;
    setLoading(true);
    try {
      const refreshed = await ordersApi.sync(displayOrder.id);
      setFullOrder(refreshed);
      showSuccess('Order status refreshed from the distributor');
      if (onRefresh) onRefresh();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to sync order status');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!displayOrder || displayOrder.status === 'cancelled' || displayOrder.status === 'completed') return;
    
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }
    
    setLoading(true);
    try {
      await ordersApi.cancel(displayOrder.id);
      showSuccess(`Order ${displayOrder.order_number} has been cancelled`);
      if (onRefresh) onRefresh();
      onClose();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!displayOrder) return;
    
    try {
      const blob = await ordersApi.downloadPdf(displayOrder.id);
      const url = window.URL.createObjectURL(blob as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PO_${displayOrder.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showSuccess('PDF downloaded successfully');
    } catch (err: any) {
      showError('Failed to download PDF');
    }
  };

  const handleDuplicate = async () => {
    if (!displayOrder) return;
    
    setLoading(true);
    try {
      const newOrder = await ordersApi.duplicate(displayOrder.id);
      showSuccess(`Order duplicated as ${newOrder.order_number}`);
      if (onDuplicate) onDuplicate(newOrder);
      if (onRefresh) onRefresh();
      onClose();
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to duplicate order');
    } finally {
      setLoading(false);
    }
  };

  // total_price is already the line total (quantity × unit price); only fall
  // back to multiplying when the server didn't send one.
  const lineTotal = (item: { total_price?: string; unit_price?: string; quantity?: number }) =>
    item.total_price
      ? parseFloat(item.total_price)
      : parseFloat(item.unit_price || '0') * (item.quantity || 1);

  // Calculate subtotal from items
  const subtotal = displayOrder.items?.reduce((sum, item) => sum + lineTotal(item), 0) || 0;

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog data-sentry-block sx={{ width: '90%', maxWidth: 800, maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        
        {fetchingOrder ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <Typography>Loading order details...</Typography>
          </Box>
        ) : (
          <>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography level="h3">
              Order {displayOrder.order_number}
            </Typography>
            <Chip
              color={getStatusColor(displayOrder.status)}
              size="md"
            >
              {displayOrder.status.charAt(0).toUpperCase() + displayOrder.status.slice(1)}
            </Chip>
          </Box>
          <Typography level="body-sm" color="neutral">
            Created on {formatDate(displayOrder.created_at)} by {displayOrder.created_by_name || 'Unknown'}
          </Typography>
        </Box>

        <Divider />

        {/* Order Information */}
        <Box sx={{ my: 3 }}>
          <Stack spacing={2}>
            {isApiOrder && displayOrder.submission_method === 'api' && (
              <Card variant="outlined">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Typography level="title-md">Distributor API Status</Typography>
                  {(displayOrder.status === 'submitted' || displayOrder.status === 'processing') && (
                    <Button
                      size="sm"
                      variant="outlined"
                      startDecorator={<SyncIcon />}
                      loading={loading}
                      onClick={handleSync}
                    >
                      Sync now
                    </Button>
                  )}
                </Box>
                <Stack spacing={1}>
                  <Typography level="body-md">
                    <strong>Confirmation #:</strong> {displayOrder.confirmation_number || 'Pending'}
                  </Typography>
                  {displayOrder.external_order_number && (
                    <Typography level="body-md">
                      <strong>Distributor order #:</strong> {displayOrder.external_order_number}
                    </Typography>
                  )}
                  {displayOrder.external_status && (
                    <Typography level="body-md" component="div">
                      <strong>Distributor status:</strong>{' '}
                      <Chip size="sm" variant="soft" color="primary">{displayOrder.external_status}</Chip>
                    </Typography>
                  )}
                  {displayOrder.last_status_sync_at && (
                    <Typography level="body-sm" color="neutral">
                      Last synced {new Date(displayOrder.last_status_sync_at).toLocaleString()}
                    </Typography>
                  )}
                  {/* Some distributors (SRS) return a live delivery-tracking
                      link once the order is on a truck. */}
                  {typeof displayOrder.provider_metadata?.srs_tracking_url === 'string' && (
                    <Typography level="body-sm">
                      <Link
                        href={displayOrder.provider_metadata.srs_tracking_url as string}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Track this delivery
                      </Link>
                    </Typography>
                  )}
                  {/* A queued order has no distributor number yet — say so
                      rather than showing an empty confirmation field. */}
                  {!displayOrder.confirmation_number
                    && !displayOrder.external_order_number
                    && typeof displayOrder.provider_metadata?.srs_queue_id === 'string' && (
                    <Typography level="body-sm" color="neutral">
                      Queued at the distributor — the order number is assigned shortly.
                    </Typography>
                  )}
                </Stack>
              </Card>
            )}

            <SubmissionResolution key={displayOrder.id} order={displayOrder} onResolved={() => { fetchFullOrderDetails(); onRefresh?.(); }} />
            {isApiOrder && displayOrder.status !== 'draft' && (
              <Alert color="neutral">To change or cancel this order, contact the distributor branch. ApexOS continues tracking the branch’s status.</Alert>
            )}
            {displayOrder.api_error && !displayOrder.api_submission_state && displayOrder.status === 'draft' && (
              <Alert color="danger" variant="soft">
                Last submission failed: {displayOrder.api_error}
              </Alert>
            )}

            <Card variant="outlined">
              <Typography level="title-md" sx={{ mb: 2 }}>Distributor Information</Typography>
              <Stack spacing={1}>
                <Typography level="body-md">
                  <strong>Company:</strong> {displayOrder.distributor_details?.name || 'N/A'}
                </Typography>
                {displayOrder.distributor_details?.sales_rep_name && (
                  <Typography level="body-md">
                    <strong>Sales Rep:</strong> {displayOrder.distributor_details.sales_rep_name}
                  </Typography>
                )}
                {displayOrder.distributor_details?.phone_number && (
                  <Typography level="body-md">
                    <strong>Phone:</strong> {displayOrder.distributor_details.phone_number}
                  </Typography>
                )}
                {displayOrder.distributor_details?.default_order_email && (
                  <Typography level="body-md">
                    <strong>Email:</strong> {displayOrder.distributor_details.default_order_email}
                  </Typography>
                )}
              </Stack>
            </Card>

            {/* Delivery Information */}
            {(displayOrder.delivery_address || displayOrder.requested_delivery_date
              || displayOrder.ship_to_number || displayOrder.po_number) && (
              <Card variant="outlined">
                <Typography level="title-md" sx={{ mb: 2 }}>Delivery Information</Typography>
                <Stack spacing={1}>
                  {displayOrder.po_number && (
                    <Typography level="body-md">
                      <strong>PO Number:</strong> {displayOrder.po_number}
                    </Typography>
                  )}
                  {displayOrder.ship_to_number && (
                    <Typography level="body-md">
                      <strong>Ship-To Account:</strong> {displayOrder.ship_to_number}
                    </Typography>
                  )}
                  {displayOrder.branch_number && (
                    <Typography level="body-md">
                      <strong>Branch:</strong> {displayOrder.branch_name || `Branch ${displayOrder.branch_number}`}
                    </Typography>
                  )}
                  {displayOrder.delivery_service && (
                    <Typography level="body-md">
                      <strong>Service:</strong> {isSrs ? SRS_SERVICE_LABELS[displayOrder.delivery_service] || displayOrder.delivery_service : deliveryServiceLabel(displayOrder.delivery_service)}
                    </Typography>
                  )}
                  {displayOrder.delivery_appointment
                    && (displayOrder.delivery_appointment as any).type_code && (
                    <Typography level="body-md">
                      <strong>{isSrs && displayOrder.delivery_service === 'WILLCALL' ? 'Pickup time:' : 'Time window:'}</strong>{' '}
                      {isSrs && displayOrder.delivery_service === 'WILLCALL' ? '' : isSrs && (displayOrder.delivery_appointment as any).type_code === 'ANYTIME' ? 'Anytime' : appointmentTypeLabel((displayOrder.delivery_appointment as any).type_code)}
                      {(displayOrder.delivery_appointment as any).from_time
                        ? `${isSrs && displayOrder.delivery_service === 'WILLCALL' ? '' : ' from '}${(displayOrder.delivery_appointment as any).from_time}`
                        : ''}
                      {(displayOrder.delivery_appointment as any).to_time
                        ? ` to ${(displayOrder.delivery_appointment as any).to_time}`
                        : ''}
                    </Typography>
                  )}
                  {!!displayOrder.provider_metadata?.contact_name && (
                    <Typography level="body-md"><strong>Jobsite contact:</strong> {String(displayOrder.provider_metadata.contact_name)}</Typography>
                  )}
                  {!!displayOrder.provider_metadata?.contact_phone && (
                    <Typography level="body-md"><strong>Contact phone:</strong> {String(displayOrder.provider_metadata.contact_phone)}</Typography>
                  )}
                  {!!displayOrder.provider_metadata?.contact_email && (
                    <Typography level="body-md"><strong>Contact email:</strong> {String(displayOrder.provider_metadata.contact_email)}</Typography>
                  )}
                  {displayOrder.delivery_appointment?.instructions && (
                    <Typography level="body-md" sx={{ whiteSpace: 'pre-wrap' }}><strong>Delivery instructions:</strong> {displayOrder.delivery_appointment.instructions}</Typography>
                  )}
                  {displayOrder.job_number && (
                    <Typography level="body-md">
                      <strong>Job:</strong> {displayOrder.job_number}
                    </Typography>
                  )}
                  {displayOrder.requested_delivery_date && (
                    <Typography level="body-md">
                      <strong>{isSrs && displayOrder.delivery_service === 'WILLCALL' ? 'Pickup date:' : 'Requested Delivery Date:'}</strong> {isSrs && displayOrder.requested_delivery_date === SRS_TBD_DATE ? 'TBD' : formatDate(displayOrder.requested_delivery_date)}
                    </Typography>
                  )}
                  {displayOrder.delivery_address && (
                    <Box>
                      <Typography level="body-md">
                        <strong>Delivery Address:</strong>
                      </Typography>
                      <Typography level="body-md">
                        {displayOrder.delivery_address.address || 'N/A'}
                      </Typography>
                      {(displayOrder.delivery_address.city || displayOrder.delivery_address.state || displayOrder.delivery_address.zip) && (
                        <Typography level="body-md">
                          {[displayOrder.delivery_address.city, displayOrder.delivery_address.state, displayOrder.delivery_address.zip]
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </Box>

        {/* Order Items */}
        <Box sx={{ my: 3 }}>
          <Typography level="title-md" sx={{ mb: 2 }}>Order Items</Typography>
          {displayOrder.items && displayOrder.items.length > 0 ? (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="sm">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Product ID</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {displayOrder.items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.product_name}</td>
                      <td>{item.product_id || '-'}</td>
                      <td>
                        {item.quantity} {item.unit_of_measure || ''}
                      </td>
                      <td>{formatCurrency(item.unit_price)}</td>
                      <td>{formatCurrency(lineTotal(item))}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={4} style={{ textAlign: 'right' }}>
                      <Typography level="title-md">Total:</Typography>
                    </td>
                    <td>
                      <Typography level="title-md">
                        {formatCurrency(displayOrder.total_amount || subtotal)}
                      </Typography>
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </Box>
          ) : (
            <Alert color="neutral">No items in this order</Alert>
          )}
        </Box>

        {isSrs && <Alert color="neutral">{SRS_CHECKOUT_DISCLAIMER}</Alert>}
        {/* Order Notes */}
        {displayOrder.notes && (
          <Box sx={{ my: 3 }}>
            <Card variant="outlined">
              <Typography level="title-md" sx={{ mb: 1 }}>{isSrs ? 'Instructions to Branch' : 'Order Notes'}</Typography>
              <Typography level="body-md">{displayOrder.notes}</Typography>
            </Card>
          </Box>
        )}

        {/* Status timeline (API orders) */}
        {displayOrder.submission_method === 'api' && (
          <Box sx={{ my: 3 }}>
            {displayOrder.confirmation_number?.startsWith('MOCK-') && (
              <Alert color="warning" sx={{ mb: 2 }}>Simulated order — this order was not sent to the distributor. Status updates are simulated.</Alert>
            )}
            <Typography level="title-md" sx={{ mb: 2 }}>Status Timeline</Typography>
            <OrderStatusTimeline events={displayOrder.events || []} />
          </Box>
        )}

        {/* Timestamps */}
        {displayOrder.submitted_at && (
          <Box sx={{ my: 2 }}>
            <Typography level="body-sm" color="neutral">
              Submitted on {formatDate(displayOrder.submitted_at)}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

            {displayOrder.status === 'draft' && !isApiOrder && (
              <FormControl required sx={{ mb: 2 }}>
                <FormLabel>Send purchase order to</FormLabel>
                <Input
                  type="email"
                  value={deliveryEmail}
                  disabled={loading}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                />
                <FormHelperText>
                  Used for this order only. The distributor's saved email stays unchanged.
                </FormHelperText>
              </FormControl>
            )}
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {displayOrder.status === 'draft' && !displayOrder.api_submission_state && (
            isSrs ? <Button component="a" href={`/operations/distributors?tab=create&draft=${displayOrder.id}`}>Review live pricing</Button> : <Button
              startDecorator={<SendIcon />}
              onClick={handleSubmitOrder}
              loading={loading}
              color="primary"
            >
              {isApiOrder ? 'Submit to Distributor' : 'Submit Order'}
            </Button>
          )}

          {displayOrder.status === 'submitted' && displayOrder.submission_method !== 'api' && (
            <Button
              startDecorator={<DownloadIcon />}
              onClick={handleDownloadPdf}
              variant="outlined"
              color="primary"
            >
              Download PDF
            </Button>
          )}
          
          <Button
            startDecorator={<ContentCopyIcon />}
            onClick={handleDuplicate}
            disabled={!!displayOrder.api_submission_state}
            loading={loading}
            variant="outlined"
            color="primary"
          >
            Duplicate
          </Button>
          
          {!displayOrder.api_submission_state && (displayOrder.status === 'draft' || (displayOrder.status === 'submitted' && !isApiOrder)) && (
            <Button
              startDecorator={<CancelIcon />}
              onClick={handleCancelOrder}
              loading={loading}
              color="danger"
              variant="outlined"
            >
              Cancel Order
            </Button>
          )}
          
          <Button onClick={onClose} variant="soft">
            Close
          </Button>
        </Box>
        </>
        )}
      </ModalDialog>
    </Modal>
  );
}
